import type { OakStreamResult } from "../../stream";

export type StreamEvent = OakStreamResult["data"][number];

/**
 * Stream changes this close together are handled as one: a burst of new notifications
 * gets one refetch, one bounce and one toast instead of one of each per notification.
 * Emitters write notifications one at a time, so a burst arrives about 0.6s apart.
 */
const STREAM_BURST_WINDOW_MS = 1000;

/** A steady stream still settles this often, rather than waiting for a quiet moment. */
const STREAM_BURST_MAX_WAIT_MS = 3000;

interface NotificationChangedPayload {
  notificationUserId: string | null;
  tenantId: string | null;
  reason: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNotificationChangedPayload(value: unknown): value is NotificationChangedPayload {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNullableString(value.notificationUserId) &&
    isNullableString(value.tenantId) &&
    typeof value.reason === "string"
  );
}

/** The events that arrived after the last one handled, oldest first. */
function eventsAfter(events: readonly StreamEvent[], handledId: string | null): StreamEvent[] {
  const handledIndex = events.findLastIndex((event) => event.id === handledId);
  return events.slice(handledIndex + 1);
}

/**
 * The events that arrived since `cursor`, advancing it. The cursor stays unset until the
 * observer attaches to the shared stream after the first render; a remounted bell is then
 * handed the stream's cached history, which it skips, so only what arrives after counts.
 */
export function takeArrivals(
  stream: Pick<OakStreamResult, "status" | "data">,
  cursor: { current: string | null | undefined }
): StreamEvent[] {
  if (cursor.current === undefined) {
    if (stream.status !== "closed") {
      cursor.current = stream.data.at(-1)?.id ?? null;
    }
    return [];
  }
  const arrived = eventsAfter(stream.data, cursor.current);
  cursor.current = arrived.at(-1)?.id ?? cursor.current;
  return arrived;
}

/** What a stream event means for the bell, or null for anything that isn't a notification change. */
export function readNotificationChange(
  event: StreamEvent
): { notificationUserId: string | null; createdId: string | null } | null {
  if (event.event !== "notification.changed" || !isNotificationChangedPayload(event.data)) {
    return null;
  }
  const { notificationUserId, reason } = event.data;
  return { notificationUserId, createdId: reason === "created" ? notificationUserId : null };
}

/**
 * What a batch of stream events asks of the bell: toast the created notifications,
 * and refresh if anything changed elsewhere. `consumeOwnEcho` uses up each echo of a
 * change made in this tab, which is already reflected locally.
 */
export function sortArrivals(
  events: readonly StreamEvent[],
  consumeOwnEcho: (notificationUserId: string) => boolean
): { createdIds: string[]; changedElsewhere: boolean } {
  const createdIds: string[] = [];
  let changedElsewhere = false;
  for (const change of events.map(readNotificationChange)) {
    if (!change) {
      continue;
    }
    if (change.createdId) {
      createdIds.push(change.createdId);
    }
    if (!change.notificationUserId || !consumeOwnEcho(change.notificationUserId)) {
      changedElsewhere = true;
    }
  }
  return { createdIds, changedElsewhere };
}

/** How long to wait before settling a burst that started at `startedAt`. */
export function burstSettleDelay(startedAt: number, now: number): number {
  const untilMaxWait = startedAt + STREAM_BURST_MAX_WAIT_MS - now;
  return Math.max(0, Math.min(STREAM_BURST_WINDOW_MS, untilMaxWait));
}
